/**
 * ERP Activations Tab Component
 * Emergency activation history and management
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle } from 'lucide-react';
import { EMERGENCY_TYPES } from './ERPDashboardTab';

export default function ERPActivationsTab({ 
  activations,
  onResolveEmergency
}) {
  return (
    <div className="space-y-4">
      {activations.map(activation => {
        const config = EMERGENCY_TYPES[activation.emergency_type] || EMERGENCY_TYPES.other;
        return (
          <Card key={activation.id} className={`border ${activation.status === 'active' ? 'border-red-500 bg-red-500/10' : 'border-slate-700 bg-slate-800/50'}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <config.icon className={`w-6 h-6 text-${config.color}-400`} />
                  <div>
                    <CardTitle className="text-white">{activation.title}</CardTitle>
                    <CardDescription>{activation.description}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={activation.status === 'active' ? 'bg-red-500 text-white' : 'bg-emerald-500/20 text-emerald-300'}>
                    {activation.status}
                  </Badge>
                  {activation.status === 'active' && (
                    <Button size="sm" variant="outline" onClick={() => onResolveEmergency(activation.id)}>
                      Resolve
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 text-sm text-slate-400 mb-4">
                <span>Level: {activation.response_level}</span>
                <span>Location: {activation.location || 'N/A'}</span>
                <span>Activated: {new Date(activation.activated_at).toLocaleString()}</span>
              </div>
              
              {/* Timeline */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-slate-300">Timeline</h4>
                {activation.timeline?.slice(0, 5).map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5" />
                    <div>
                      <span className="text-white">{entry.action}</span>
                      <span className="text-slate-500 ml-2">by {entry.by}</span>
                      <span className="text-slate-600 ml-2 text-xs">{new Date(entry.timestamp).toLocaleTimeString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
      
      {activations.length === 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Emergency Activations</h3>
            <p className="text-slate-400">All systems operating normally</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
